'use client'

import * as NavigationMenu from '@radix-ui/react-navigation-menu'
import Link from 'next/link'
import { forwardRef, type ForwardedRef } from 'react'

import Icon, { type IconPropsType } from '@/legacy-src/components/icons/Icon'
import { cn } from '@/legacy-src/lib/utils'

import { HeaderBasicNavigationLink } from '@/src/domains/header/components/Header/HeaderBasicNavigationLink'
import { useHeaderNavigationItem } from '@/src/domains/header/components/Header/HeaderNavigationItem/HeaderNavigationItem.context'

export type HeaderNavigationButtonProps =
  | (Omit<NavigationMenu.NavigationMenuLinkProps, 'href' | 'onSelect'> & {
      href: string
    })
  | (NavigationMenu.NavigationMenuTriggerProps & {
      iconColor?: IconPropsType['color']
    })

const isLink = (props: HeaderNavigationButtonProps): props is Extract<HeaderNavigationButtonProps, { href: string }> =>
  'href' in props && typeof props.href === 'string'

export const HeaderNavigationButton = forwardRef<HTMLElement, HeaderNavigationButtonProps>((props, ref) => {
  const { trigger } = useHeaderNavigationItem()

  const className = cn(
    'group m-0 flex w-fit items-center gap-1.5 border-none bg-transparent p-0 text-secondary !no-underline hover:bg-transparent focus:bg-transparent focus:text-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 data-[state=open]:bg-transparent',
    props.className,
  )

  if (isLink(props)) {
    return (
      <HeaderBasicNavigationLink asChild className={className}>
        <Link {...props} ref={ref as ForwardedRef<HTMLAnchorElement>} />
      </HeaderBasicNavigationLink>
    )
  }

  const { iconColor, ...rest } = props

  const triggerProps =
    trigger === 'click'
      ? {
          onPointerMove: (event: React.PointerEvent) => event.preventDefault(),
          onPointerLeave: (event: React.PointerEvent) => event.preventDefault(),
          ...rest,
        }
      : rest

  return (
    <NavigationMenu.Trigger {...triggerProps} className={className} ref={ref as ForwardedRef<HTMLButtonElement>}>
      {props.children}
      <Icon
        color={iconColor}
        icon="arrows-beak"
        size={20}
        className="relative top-[1px] shrink-0 transition duration-200 group-data-[state=open]:rotate-180"
      />
    </NavigationMenu.Trigger>
  )
})

HeaderNavigationButton.displayName = 'HeaderNavigationButton'
